import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/api';
import CatalogAdmin from './CatalogAdmin';
import './AdminPage.css';
import './CatalogAdmin.css';

/* ─── Constants ─────────────────────────────────────────────── */

const ROLE_OPTIONS = [
  { value: 'moderador',        label: 'Moderador' },
  { value: 'visualizador_interno', label: 'Visualizador Interno' },
  { value: 'gestor_municipal', label: 'Gestor Municipal' },
  { value: 'prestadora',       label: 'Prestadora' },
  { value: 'sociedade',        label: 'Sociedade' },
];

const ROLE_COLORS = {
  moderador:        '#c0392b',
  visualizador_interno: '#6f42c1',
  gestor_municipal: '#2980b9',
  prestadora:       '#27ae60',
  sociedade:        '#7f8c8d',
};

/* ─── Icons ──────────────────────────────────────────────────── */

function IconUsers() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );
}

function IconLayers() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2"/>
      <polyline points="2 17 12 22 22 17"/>
      <polyline points="2 12 12 17 22 12"/>
    </svg>
  );
}

function IconArrowLeft() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  );
}

function IconMap() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21 3 6"/>
    </svg>
  );
}

/* ─── RoleBadge ──────────────────────────────────────────────── */

function RoleBadge({ role }) {
  const label = ROLE_OPTIONS.find((r) => r.value === role)?.label || role;
  return (
    <span className="ap-role-badge" style={{ background: ROLE_COLORS[role] || '#555' }}>
      {label}
    </span>
  );
}

/* ─── UserList ───────────────────────────────────────────────── */

function UserList({ users, total, page, limit, loading, error, onPageChange, onEdit, onDeactivate, onActivate, onCreateClick }) {
  const totalPages = Math.ceil(total / limit);

  return (
    <>
      <div className="ap-section-toolbar">
        <div>
          <h2 className="ap-section-title">Usuários</h2>
          <p className="ap-section-sub">{total} usuário{total !== 1 ? 's' : ''} cadastrados</p>
        </div>
        <button className="ap-btn ap-btn--primary" onClick={onCreateClick}>
          + Novo Usuário
        </button>
      </div>

      {error && <div className="ap-alert ap-alert--error">{error}</div>}

      {loading ? (
        <div className="ap-loading"><span className="ap-spinner" /> Carregando…</div>
      ) : (
        <>
          <div className="ap-table-wrap">
            <table className="ap-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Papel</th>
                  <th>Organização</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr><td colSpan={6} className="ap-table-empty">Nenhum usuário cadastrado.</td></tr>
                )}
                {users.map((u) => (
                  <tr key={u.id} className={!u.active ? 'ap-row--inactive' : ''}>
                    <td className="ap-td-name">{u.name}</td>
                    <td className="ap-td-email">{u.email}</td>
                    <td><RoleBadge role={u.role} /></td>
                    <td className="ap-td-org">{u.organization || '—'}</td>
                    <td>
                      <span className={`ap-status ${u.active ? 'ap-status--active' : 'ap-status--inactive'}`}>
                        {u.active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td>
                      <div className="ap-row-actions">
                        <button className="ap-row-btn ap-row-btn--edit" onClick={() => onEdit(u)}>Editar</button>
                        {u.active
                          ? <button className="ap-row-btn ap-row-btn--danger"  onClick={() => onDeactivate(u.id)}>Desativar</button>
                          : <button className="ap-row-btn ap-row-btn--success" onClick={() => onActivate(u.id)}>Ativar</button>
                        }
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="ap-pagination">
              <button className="ap-pag-btn" onClick={() => onPageChange(page - 1)} disabled={page <= 1}>← Anterior</button>
              <span className="ap-pag-info">Página {page} de {totalPages}</span>
              <button className="ap-pag-btn" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>Próxima →</button>
            </div>
          )}
        </>
      )}
    </>
  );
}

/* ─── UserForm ───────────────────────────────────────────────── */

function UserForm({ initialData, onSaved, onCancel }) {
  const isEdit = !!initialData;
  const [form, setForm] = useState({
    name:         initialData?.name         || '',
    email:        initialData?.email        || '',
    role:         initialData?.role         || 'sociedade',
    organization: initialData?.organization || '',
    password:     '',
  });
  const [error,   setError]   = useState(null);
  const [loading, setLoading] = useState(false);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const payload = {
        name:         form.name,
        email:        form.email,
        role:         form.role,
        organization: form.organization || undefined,
      };
      if (form.password) payload.password = form.password;
      if (isEdit) {
        await adminAPI.updateUser(initialData.id, payload);
      } else {
        if (!form.password) { setError('Senha é obrigatória.'); setLoading(false); return; }
        await adminAPI.createUser({ ...payload, password: form.password });
      }
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.error || 'Erro ao salvar usuário.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="ap-section-toolbar">
        <div>
          <h2 className="ap-section-title">{isEdit ? 'Editar Usuário' : 'Novo Usuário'}</h2>
          <p className="ap-section-sub">{isEdit ? `Editando ${initialData.name}` : 'Preencha os dados do novo usuário'}</p>
        </div>
        <button className="ap-btn ap-btn--ghost" onClick={onCancel}>
          <IconArrowLeft /> Cancelar
        </button>
      </div>

      {error && <div className="ap-alert ap-alert--error">{error}</div>}

      <div className="ap-form-card">
        <form className="ap-form" onSubmit={handleSubmit} noValidate>
          <div className="ap-form-grid">
            <div className="ap-form-row">
              <label className="ap-label">Nome completo *</label>
              <input className="ap-input" name="name" value={form.name} onChange={handleChange} required disabled={loading} placeholder="Nome Sobrenome" />
            </div>
            <div className="ap-form-row">
              <label className="ap-label">Email *</label>
              <input className="ap-input" name="email" type="email" value={form.email} onChange={handleChange} required disabled={loading} placeholder="email@exemplo.com" />
            </div>
            <div className="ap-form-row">
              <label className="ap-label">Papel *</label>
              <select className="ap-select" name="role" value={form.role} onChange={handleChange} disabled={loading}>
                {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="ap-form-row">
              <label className="ap-label">Organização</label>
              <input className="ap-input" name="organization" value={form.organization} onChange={handleChange} disabled={loading} placeholder="Prefeitura / Empresa (opcional)" />
            </div>
            <div className="ap-form-row ap-form-row--full">
              <label className="ap-label">{isEdit ? 'Nova Senha (vazio = manter atual)' : 'Senha *'}</label>
              <input className="ap-input" name="password" type="password" value={form.password} onChange={handleChange} disabled={loading} placeholder="Mínimo 8 caracteres" autoComplete="new-password" />
            </div>
          </div>
          <div className="ap-form-actions">
            <button type="submit" className="ap-btn ap-btn--primary" disabled={loading}>
              {loading ? 'Salvando…' : (isEdit ? 'Salvar Alterações' : 'Criar Usuário')}
            </button>
            <button type="button" className="ap-btn ap-btn--ghost" onClick={onCancel} disabled={loading}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

/* ─── UsersSection ───────────────────────────────────────────── */

function UsersSection() {
  const [view,       setView]       = useState('list');
  const [users,      setUsers]      = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [editTarget, setEditTarget] = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const LIMIT = 20;

  const loadUsers = useCallback(async (p = 1) => {
    setLoading(true); setError(null);
    try {
      const res = await adminAPI.listUsers(p, LIMIT);
      setUsers(res.data.users);
      setTotal(res.data.total);
      setPage(p);
    } catch (err) {
      setError(err?.response?.data?.error || 'Erro ao carregar usuários.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadUsers(1); }, [loadUsers]);

  async function handleDeactivate(userId) {
    if (!window.confirm('Desativar este usuário?')) return;
    try { await adminAPI.deactivateUser(userId); loadUsers(page); }
    catch (err) { alert(err?.response?.data?.error || 'Erro.'); }
  }

  async function handleActivate(userId) {
    try { await adminAPI.activateUser(userId); loadUsers(page); }
    catch (err) { alert(err?.response?.data?.error || 'Erro.'); }
  }

  if (view === 'create') return (
    <UserForm onSaved={() => { setView('list'); loadUsers(1); }} onCancel={() => setView('list')} />
  );
  if (view === 'edit') return (
    <UserForm initialData={editTarget} onSaved={() => { setView('list'); loadUsers(page); }} onCancel={() => setView('list')} />
  );

  return (
    <UserList
      users={users} total={total} page={page} limit={LIMIT}
      loading={loading} error={error}
      onPageChange={loadUsers}
      onEdit={(u) => { setEditTarget(u); setView('edit'); }}
      onDeactivate={handleDeactivate}
      onActivate={handleActivate}
      onCreateClick={() => setView('create')}
    />
  );
}

/* ─── NavItem ────────────────────────────────────────────────── */

function NavItem({ icon, label, active, onClick }) {
  return (
    <button className={`ap-nav-item${active ? ' ap-nav-item--active' : ''}`} onClick={onClick}>
      <span className="ap-nav-icon">{icon}</span>
      <span className="ap-nav-label">{label}</span>
    </button>
  );
}

/* ─── AdminPage ──────────────────────────────────────────────── */

const NAV = [
  { id: 'usuarios', label: 'Usuários', icon: <IconUsers /> },
  { id: 'camadas',  label: 'Camadas',  icon: <IconLayers /> },
];

function AdminPage({ onClose, theme }) {
  const { user } = useAuth();
  const [section, setSection] = useState('usuarios');

  return (
    <div className={`admin-page ${theme === 'dark' ? 'theme-dark' : 'theme-light'}`}>

      {/* ── Top bar ── */}
      <header className="ap-header">
        <div className="ap-header-left">
          <img src="/Logo_2.png" alt="Saneamento Vivo" className="ap-header-logo" />
          <div className="ap-header-divider" />
          <span className="ap-header-title">Painel Administrativo</span>
        </div>
        <div className="ap-header-right">
          {user && (
            <span className="ap-header-user">
              <span className="ap-header-role" style={{ background: ROLE_COLORS[user.role] || '#555' }}>
                {ROLE_OPTIONS.find((r) => r.value === user.role)?.label || user.role}
              </span>
              {user.name}
            </span>
          )}
          <button className="ap-header-back-btn" onClick={onClose}>
            <IconArrowLeft />
            Voltar ao Mapa
            <IconMap />
          </button>
        </div>
      </header>

      <div className="ap-body">

        {/* ── Left nav ── */}
        <nav className="ap-nav">
          <p className="ap-nav-label-group">Gestão</p>
          {NAV.map((item) => (
            <NavItem
              key={item.id}
              icon={item.icon}
              label={item.label}
              active={section === item.id}
              onClick={() => setSection(item.id)}
            />
          ))}

          <div className="ap-nav-footer">
            <button className="ap-nav-footer-btn" onClick={onClose}>
              <IconArrowLeft />
              Voltar ao Mapa
            </button>
          </div>
        </nav>

        {/* ── Content ── */}
        <main className={`ap-content${section === 'camadas' ? ' ap-content--catalog' : ''}`}>

          {section === 'usuarios' && (
            <div className="ap-section">
              <UsersSection />
            </div>
          )}

          {section === 'camadas' && (
            <div className="ap-section ap-section--catalog">
              <div className="ap-section-toolbar">
                <div>
                  <h2 className="ap-section-title">Organização de Camadas</h2>
                  <p className="ap-section-sub">Atribua camadas publicadas no GeoServer ao catálogo hierárquico</p>
                </div>
              </div>
              <div className="ap-catalog-wrap">
                <CatalogAdmin />
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
}

export default AdminPage;
